// OrderFlow.jsx
//
// The Distributor Orders grid: one column per order, rows for order fields,
// SKU quantities, and totals. Three tabs in the header — Orders / Forecast
// Comparison / Summary.
//
// Ported from the source app (where it lived inside the
// forecasting module). Adaptations for Brand Command:
//   • Supabase Auth + RLS service (ordersService) instead of a service-role
//     edge function; `canEdit` prop instead of the manage_orders permission.
//   • DEFERRED (backends don't exist here yet): QuickBooks invoice posting,
//     the co-packer PO PDF generator (production-hub), and the order-shipped /
//     invoice-posted notification e-mails — all removed from this port.
//   • The column drag-reorder (dnd-kit) is dropped to keep the module
//     dependency-free; orders render in sort_order.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  loadOrderFlowData,
  updateOrderField,
  upsertOrderItem,
  createOrderFlow,
  deleteOrderFlow,
  uploadOrderPOFile,
  getOrderPOFileUrl,
  clearOrderPOFile,
  saveOrderDistributorPo,
  loadAutoPOSettings,
  saveAutoPOSettings,
  loadStatesWithRegions,
} from "../utils/ordersService";
import OrderForecastComparison from "./OrderForecastComparison";
import OrderSummary from "./OrderSummary";
import MobileFilterDrawer from "./MobileFilterDrawer";
import { exportOrdersToExcel } from "./ordersExport";
import { getSkuGroup, sortSkus } from "./skuGrouping";
import { monthLabel, toMonthKey, toMonthStart } from "../utils/monthDate.js";
import { skuSize } from "../utils/sizeGroups";
import "./do-tokens.css"; // must precede OrderFlow.css — defines the ported --amber/--navy/--table-* names
import "./OrderFlow.css";

const STATUS_OPTIONS = [
  "Order Received",
  "On Hold / Review Needed",
  "Pickup Requested",
  "Pickup Confirmed",
  "Ready Date Estimated",
  "Shipped",
];

const FIELD_ROWS = [
  { key: "state",             label: "State",             type: "select",   combobox: false },
  { key: "distributor",       label: "Distributor",       type: "select",   combobox: false },
  { key: "warehouse",         label: "Warehouse",         type: "text",     combobox: true  },
  { key: "status",            label: "Status",            type: "select",   combobox: false },
  { key: "target_ship_month", label: "Target Ship Month", type: "month",    combobox: false },
  { key: "po_receipt_date",   label: "PO Receipt Date",   type: "date",     combobox: false },
  { key: "pickup_date",       label: "Pickup Date",       type: "date",     combobox: false },
  { key: "copacker_po",       label: "CoPacker PO #",     type: "text",     combobox: false },
  { key: "distributor_po",    label: "Dist PO #",         type: "text",     combobox: false },
  { key: "notes",             label: "Notes",             type: "text",     combobox: false },
];

const FILTER_CARDS = [
  { status: "Order Received",         label: "Order Received",   colorClass: "of-fc-purple" },
  { status: "On Hold / Review Needed",label: "On Hold / Review", colorClass: "of-fc-red"    },
  { status: "Ready Date Estimated",   label: "Ready Date Est.",  colorClass: "of-fc-blue"   },
  { status: "Pickup Requested",       label: "Pickup Requested", colorClass: "of-fc-gray"   },
  { status: "Pickup Confirmed",       label: "Pickup Confirmed", colorClass: "of-fc-amber"  },
  { status: "Shipped",                label: "Shipped",          colorClass: "of-fc-green"  },
];

function statusKey(s) {
  if (s === "Order Received")          return "received";
  if (s === "On Hold / Review Needed") return "onhold";
  if (s === "Shipped")                 return "shipped";
  if (s === "Pickup Confirmed")        return "confirmed";
  if (s === "Pickup Requested")        return "requested";
  if (s === "Ready Date Estimated")    return "ready";
  return "default";
}

export default function OrderFlow({ onHome, canEdit = false }) {
  const [viewMode, setViewMode] = useState(() => sessionStorage.getItem("doViewMode") || "orders"); // "orders" | "comparison" | "summary"
  // Set by the comparison tab when the plan has no Sales Forecasting, so the
  // shared Export button can go disabled instead of writing an empty workbook.
  const [comparisonLocked, setComparisonLocked] = useState(false);
  const comparisonRef = React.useRef(null);
  const summaryRef    = React.useRef(null);

  const [orders,         setOrders]         = useState([]);
  const [items,          setItems]          = useState([]);
  const [skuList,        setSkuList]        = useState([]);
  const [sizes,          setSizes]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [editCell,       setEditCell]       = useState(null);
  const [editValue,      setEditValue]      = useState("");
  const [confirmDelete,  setConfirmDelete]  = useState(null);
  const [adding,         setAdding]         = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  // PO attachment modal + auto-PO settings modal.
  const [poModal,        setPoModal]        = useState(null);
  const [poNumModal,     setPoNumModal]     = useState(null);
  const [showHidden,     setShowHidden]     = useState(false);
  const [statesData,     setStatesData]     = useState([]);
  const [filterRegion,   setFilterRegion]   = useState("");
  const [filterState,    setFilterState]    = useState("");
  const [filterMonth,    setFilterMonth]    = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { orders: o, items: i, skus: s, sizes: sz, packs: pk } = await loadOrderFlowData();
      setOrders(o);
      setItems(i);
      setSizes(sz ?? []);
      setSkuList(sortSkus(s, { sizeOrder: sz, packOrder: pk }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStatesWithRegions().then(setStatesData).catch(console.error); }, []);
  useEffect(() => { sessionStorage.setItem("doViewMode", viewMode); }, [viewMode]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const itemsByOrder = useMemo(() => {
    const map = {};
    items.forEach(item => {
      if (!map[item.order_id]) map[item.order_id] = {};
      map[item.order_id][item.sku_name] = item.quantity ?? 0;
    });
    return map;
  }, [items]);

  const skuSortOrders = useMemo(() => {
    const map = {};
    items.forEach(item => {
      if (map[item.sku_name] === undefined || item.sort_order < map[item.sku_name]) {
        map[item.sku_name] = item.sort_order;
      }
    });
    return map;
  }, [items]);

  const totalCases = useMemo(() => {
    const totals = {};
    orders.forEach(o => {
      const oi = itemsByOrder[o.id] ?? {};
      totals[o.id] = Object.values(oi).reduce((sum, q) => sum + (q || 0), 0);
    });
    return totals;
  }, [orders, itemsByOrder]);

  // One total per bottle size the account sells — { [orderId]: { [size]: qty } }.
  const totalBySize = useMemo(() => {
    const totals = {};
    orders.forEach(o => {
      const oi = itemsByOrder[o.id] ?? {};
      const perSize = {};
      Object.entries(oi).forEach(([skuName, qty]) => {
        const size = skuSize(skuName);
        if (!size) return;
        perSize[size] = (perSize[size] || 0) + (qty || 0);
      });
      totals[o.id] = perSize;
    });
    return totals;
  }, [orders, itemsByOrder]);

  const statusCounts = useMemo(() => {
    const counts = {};
    orders.filter(o => o.visible !== false).forEach(o => { counts[o.status] = (counts[o.status] || 0) + 1; });
    return counts;
  }, [orders]);

  const hiddenCount = useMemo(() => orders.filter(o => o.visible === false).length, [orders]);

  const filteredOrders = useMemo(() => {
    if (showHidden) return orders.filter(o => o.visible === false);
    let visible = orders.filter(o => o.visible !== false);
    if (selectedStatus) visible = visible.filter(o => o.status === selectedStatus);
    if (filterState)  visible = visible.filter(o => o.state === filterState);
    else if (filterRegion) {
      const regionStates = new Set(statesData.filter(r => r.region === filterRegion).map(r => r.state_name));
      visible = visible.filter(o => regionStates.has(o.state));
    }
    if (filterMonth) visible = visible.filter(o => toMonthStart(o.target_ship_month) === filterMonth);
    return visible;
  }, [orders, selectedStatus, showHidden, filterRegion, filterState, filterMonth, statesData]);

  const stateOptions = useMemo(
    () => [...new Set((statesData || []).map(r => r.state_name).filter(Boolean))].sort(),
    [statesData]
  );
  const distributorsByState = useMemo(() => {
    const m = {};
    for (const r of (statesData || [])) {
      if (!r.state_name || !r.distributor) continue;
      if (!m[r.state_name]) m[r.state_name] = new Set();
      m[r.state_name].add(r.distributor);
    }
    const out = {};
    for (const [k, v] of Object.entries(m)) out[k] = [...v].sort();
    return out;
  }, [statesData]);
  const warehouseOptions = useMemo(() => [...new Set(orders.map(o => o.warehouse).filter(Boolean))].sort(), [orders]);

  const regionOptions = useMemo(
    () => [...new Set(statesData.map(r => r.region).filter(Boolean))].sort(),
    [statesData]
  );
  const stateFilterOptions = useMemo(() => {
    if (!filterRegion) return [...new Set(statesData.map(r => r.state_name).filter(Boolean))].sort();
    return statesData.filter(r => r.region === filterRegion).map(r => r.state_name).sort();
  }, [statesData, filterRegion]);

  // One entry per (month, year) actually in use, newest last. Was a filter over
  // the bare MONTHS list, which showed one "January" no matter how many years
  // of orders sat behind it (backlog 237). Values are stored month-starts.
  const monthFilterOptions = useMemo(() => {
    const used = [...new Set(orders.map(o => o.target_ship_month).filter(Boolean).map(toMonthStart))];
    return used.sort().map((value) => ({ value, label: monthLabel(value) }));
  }, [orders]);

  const displaySkus = skuList;

  // ── Editing ───────────────────────────────────────────────────────────────
  const startEdit  = (orderId, field, value) => {
    if (!canEdit) return;
    setEditCell({ orderId, field }); setEditValue(value ?? "");
  };
  const cancelEdit = () => { setEditCell(null); setEditValue(""); };

  const commitEdit = async (orderId, field) => {
    if (!editCell || editCell.orderId !== orderId || editCell.field !== field) return;
    const isField = FIELD_ROWS.some(r => r.key === field);
    try {
      if (isField) {
        // The month control speaks "YYYY-MM"; the column is a date pinned to
        // the first of that month (migration 0085).
        const raw = editValue.trim();
        const val = field === "target_ship_month"
          ? (toMonthStart(raw) || null)
          : (raw || null);
        await updateOrderField(orderId, field, val);
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, [field]: val } : o));
      } else {
        const qty = parseInt(editValue, 10) || 0;
        const sortOrder = skuSortOrders[field] ?? displaySkus.indexOf(field);
        await upsertOrderItem(orderId, field, qty, sortOrder);
        setItems(prev => {
          const exists = prev.find(i => i.order_id === orderId && i.sku_name === field);
          if (exists) return prev.map(i => i.order_id === orderId && i.sku_name === field ? { ...i, quantity: qty } : i);
          return [...prev, { id: `${orderId}-${field}`, order_id: orderId, sku_name: field, quantity: qty, sort_order: sortOrder }];
        });
      }
    } catch (err) {
      console.error("Save failed:", err.message);
    }
    setEditCell(null);
    setEditValue("");
  };

  const handleAddOrder = async () => {
    if (adding) return;
    setAdding(true);
    try { await createOrderFlow(); await load(); }
    catch (err) { console.error("Add order failed:", err.message); }
    finally { setAdding(false); }
  };

  // ── Distributor PO attachment modal ─────────────────────────────────────
  const openPoModal = async (order) => {
    setPoModal({
      stage:          "edit",
      order,
      distributorPo:  order.distributor_po || "",
      fileObj:        null,
      currentUrl:     null,
      currentFile:    null,
      loadingPreview: !!order.po_file_path,
      error:          null,
    });
    if (order.po_file_path) {
      try {
        const res = await getOrderPOFileUrl(order.id);
        setPoModal(p => (p && p.order.id === order.id) ? { ...p, currentUrl: res?.url || null, currentFile: res?.file || null, loadingPreview: false } : p);
      } catch (err) {
        setPoModal(p => (p && p.order.id === order.id) ? { ...p, loadingPreview: false, error: `Could not load PO preview: ${err.message}` } : p);
      }
    }
  };

  const handlePoModalSave = async () => {
    if (!poModal?.order?.id) return;
    setPoModal(p => p ? { ...p, stage: "saving", error: null } : p);
    const orderId = poModal.order.id;
    const newDistPo = (poModal.distributorPo ?? "").trim();
    const distChanged = newDistPo !== (poModal.order.distributor_po ?? "");
    try {
      if (poModal.fileObj) {
        const meta = await uploadOrderPOFile({
          orderId,
          file:          poModal.fileObj,
          distributorPo: distChanged ? (newDistPo || null) : undefined,
        });
        setOrders(prev => prev.map(o => o.id === orderId ? {
          ...o,
          po_file_path:         meta.po_file_path,
          po_file_name:         meta.po_file_name,
          po_file_size:         meta.po_file_size,
          po_file_content_type: meta.po_file_content_type,
          po_file_uploaded_at:  meta.po_file_uploaded_at,
          po_file_uploaded_by:  meta.po_file_uploaded_by,
          ...(distChanged ? { distributor_po: newDistPo || null } : {}),
        } : o));
      } else if (distChanged) {
        await saveOrderDistributorPo(orderId, newDistPo);
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, distributor_po: newDistPo || null } : o));
      }
      setPoModal(null);
    } catch (err) {
      setPoModal(p => p ? { ...p, stage: "edit", error: err.message } : p);
    }
  };

  const handlePoModalClear = async () => {
    if (!poModal?.order?.id) return;
    if (!window.confirm("Remove the attached PO file from this order?")) return;
    setPoModal(p => p ? { ...p, stage: "saving", error: null } : p);
    try {
      const orderId = poModal.order.id;
      await clearOrderPOFile(orderId);
      setOrders(prev => prev.map(o => o.id === orderId ? {
        ...o, po_file_path: null, po_file_name: null, po_file_size: null,
        po_file_content_type: null, po_file_uploaded_at: null, po_file_uploaded_by: null,
      } : o));
      setPoModal(p => p ? {
        ...p, stage: "edit", currentUrl: null, currentFile: null, fileObj: null,
        order: { ...p.order, po_file_path: null, po_file_name: null, po_file_size: null,
                 po_file_content_type: null, po_file_uploaded_at: null, po_file_uploaded_by: null },
      } : p);
    } catch (err) {
      setPoModal(p => p ? { ...p, stage: "edit", error: err.message } : p);
    }
  };

  // ── Auto CoPacker PO # settings modal ───────────────────────────────────
  const openPoNumModal = async () => {
    setPoNumModal({ stage: "loading", enabled: false, starting: 1, lastAssigned: 0, error: null });
    try {
      const s = await loadAutoPOSettings();
      setPoNumModal({ stage: "edit", enabled: !!s.enabled, starting: Number(s.starting) || 1, lastAssigned: Number(s.last_assigned) || 0, error: null });
    } catch (err) {
      setPoNumModal({ stage: "edit", enabled: false, starting: 1, lastAssigned: 0, error: `Could not load settings: ${err.message}` });
    }
  };

  const handlePoNumModalSave = async () => {
    if (!poNumModal) return;
    const startingNum = parseInt(poNumModal.starting, 10);
    if (!Number.isFinite(startingNum) || startingNum < 0) {
      setPoNumModal(p => p ? { ...p, error: "Starting PO # must be a non-negative whole number." } : p);
      return;
    }
    setPoNumModal(p => p ? { ...p, stage: "saving", error: null } : p);
    try {
      await saveAutoPOSettings({ enabled: !!poNumModal.enabled, starting: startingNum });
      setPoNumModal(null);
    } catch (err) {
      setPoNumModal(p => p ? { ...p, stage: "edit", error: err.message } : p);
    }
  };

  const handleDelete = async (orderId) => {
    try {
      await deleteOrderFlow(orderId);
      setOrders(prev => prev.filter(o => o.id !== orderId));
      setItems(prev => prev.filter(i => i.order_id !== orderId));
      setConfirmDelete(null);
    } catch (err) { console.error("Delete failed:", err.message); }
  };

  const handleToggleVisible = async (orderId, makeVisible) => {
    try {
      await updateOrderField(orderId, "visible", makeVisible);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, visible: makeVisible } : o));
    } catch (err) { console.error("Visibility update failed:", err.message); }
  };

  // ── Cell renderers ──────────────────────────────────────────────────────
  const getComboboxOptions = (field) => (field === "warehouse" ? warehouseOptions : null);

  const getSelectOptions = (field, order) => {
    if (field === "state")       return stateOptions;
    if (field === "distributor") return distributorsByState[order.state] || [];
    if (field === "status")      return STATUS_OPTIONS;
    return [];
  };

  const renderFieldCell = (order, row) => {
    const value     = order[row.key] ?? "";
    const isEditing = editCell?.orderId === order.id && editCell?.field === row.key;

    if (isEditing) {
      if (row.type === "select") {
        const opts = getSelectOptions(row.key, order);
        return (
          <select className="of-cell-input" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={() => commitEdit(order.id, row.key)} autoFocus>
            <option value="">— Select —</option>
            {opts.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        );
      }
      if (row.type === "month") {
        // A native month picker, not a list of month names: target_ship_month
        // carries a year now (backlog 237) and a bare "January" cannot say
        // which one. The control speaks "YYYY-MM"; commitEdit stores the
        // month-start date.
        return (
          <input
            type="month"
            className="of-cell-input"
            value={toMonthKey(editValue)}
            onChange={e => setEditValue(e.target.value)}
            onBlur={() => commitEdit(order.id, row.key)}
            autoFocus
          />
        );
      }
      const cbOptions = row.combobox ? getComboboxOptions(row.key) : null;
      const listId    = cbOptions ? `of-dl-${row.key}-${order.id}` : undefined;
      return (
        <>
          <input
            className="of-cell-input"
            type={row.type === "date" ? "date" : "text"}
            list={listId}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={() => commitEdit(order.id, row.key)}
            onKeyDown={e => { if (e.key === "Enter") commitEdit(order.id, row.key); if (e.key === "Escape") cancelEdit(); }}
            autoFocus
          />
          {cbOptions && (
            <datalist id={listId}>
              {cbOptions.map(opt => <option key={opt} value={opt} />)}
            </datalist>
          )}
        </>
      );
    }

    if (row.key === "status") {
      return (
        <span className={`of-status-badge of-status-${statusKey(value)}`} onClick={() => startEdit(order.id, row.key, value)}>
          {value || "—"}
        </span>
      );
    }

    if (row.type === "month") {
      // Stored "2026-01-01", read as "January 2026". monthLabel is deliberately
      // string arithmetic — see monthDate.js on why this must not touch Date.
      return (
        <span className="of-cell-text" onClick={() => startEdit(order.id, row.key, value)}>
          {monthLabel(value) || <span className="of-cell-empty">—</span>}
        </span>
      );
    }

    if (row.key === "distributor_po") {
      const hasFile = !!order.po_file_path;
      return (
        <span className="of-cell-text" style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "100%" }}>
          <span
            style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            onClick={() => startEdit(order.id, row.key, value)}
          >
            {value || <span className="of-cell-empty">—</span>}
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openPoModal(order); }}
            title={hasFile ? `PO attached: ${order.po_file_name || "file"} — click to view / replace` : "Attach PO file"}
            aria-label={hasFile ? "View or replace attached PO" : "Attach PO file"}
            className={"of-clip-btn" + (hasFile ? " is-attached" : "")}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.49"/>
            </svg>
          </button>
        </span>
      );
    }

    return (
      <span className="of-cell-text" onClick={() => startEdit(order.id, row.key, value)}>
        {value || <span className="of-cell-empty">—</span>}
      </span>
    );
  };

  const renderQtyCell = (order, sku) => {
    const qty       = itemsByOrder[order.id]?.[sku] ?? 0;
    const isEditing = editCell?.orderId === order.id && editCell?.field === sku;
    if (isEditing) {
      return (
        <input
          className="of-cell-input of-qty-input"
          type="number" min="0"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={() => commitEdit(order.id, sku)}
          onKeyDown={e => { if (e.key === "Enter") commitEdit(order.id, sku); if (e.key === "Escape") cancelEdit(); }}
          autoFocus
        />
      );
    }
    return (
      <span className={`of-qty-text${qty > 0 ? " of-qty-nonzero" : ""}`} onClick={() => startEdit(order.id, sku, String(qty))}>
        {qty > 0 ? qty : <span className="of-qty-zero">—</span>}
      </span>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="of-page">
      <div className="of-page-header">
        <div className="of-page-title-row">
          <button className="of-back-btn" onClick={onHome}>← Back</button>
          <h1 className="of-page-title">Current Orders</h1>
        </div>
        <div className="of-header-actions">
          <div className="of-view-toggle">
            <button className={`of-view-btn${viewMode === "orders" ? " of-view-btn-active" : ""}`} onClick={() => setViewMode("orders")}>Orders</button>
            <button className={`of-view-btn${viewMode === "comparison" ? " of-view-btn-active" : ""}`} onClick={() => setViewMode("comparison")}>Forecast Comparison</button>
            <button className={`of-view-btn${viewMode === "summary" ? " of-view-btn-active" : ""}`} onClick={() => setViewMode("summary")}>Summary</button>
          </div>
          <button
            className="of-export-btn"
            onClick={() => {
              if (viewMode === "comparison") {
                // Belt as well as braces: the button is disabled below, but a
                // locked tab must never be able to write an empty workbook.
                if (comparisonLocked) return;
                comparisonRef.current?.exportToExcel().catch(console.error);
              }
              else if (viewMode === "summary") summaryRef.current?.exportToExcel().catch(console.error);
              else exportOrdersToExcel({ orders: filteredOrders, items, displaySkus, sizes }).catch(console.error);
            }}
            disabled={
              (viewMode === "orders" && (loading || filteredOrders.length === 0)) ||
              (viewMode === "comparison" && comparisonLocked)
            }
            title={viewMode === "comparison" && comparisonLocked
              ? "Nothing to export — Forecast Comparison needs Sales Forecasting"
              : "Export current view to Excel"}
          >
            ↓ Export
          </button>
          {canEdit && viewMode !== "summary" && (
            <button className="of-add-btn" onClick={handleAddOrder} disabled={adding}>
              {adding ? "Adding…" : "+ Add New Order"}
            </button>
          )}
        </div>
      </div>

      <div className="of-page-body">
      {viewMode === "comparison" ? (
        <OrderForecastComparison ref={comparisonRef} canEdit={canEdit} onLockedChange={setComparisonLocked} />
      ) : viewMode === "summary" ? (
        <OrderSummary ref={summaryRef} canEdit={canEdit} />
      ) : (
        <>
          {!loading && !error && (
            <div className="of-filter-bar">
              <span className="of-filter-section-label">STATUS</span>
              <button
                className={`of-status-pill of-sp-all${!showHidden && selectedStatus === null ? " of-sp-all-active" : ""}`}
                onClick={() => { setShowHidden(false); setSelectedStatus(null); }}
              >
                <span className="of-sp-count">{orders.filter(o => o.visible !== false).length}</span>
                <span className="of-sp-label">All</span>
              </button>
              {FILTER_CARDS.map(fc => {
                const count    = statusCounts[fc.status] || 0;
                const isActive = !showHidden && selectedStatus === fc.status;
                return (
                  <button
                    key={fc.label}
                    className={`of-status-pill ${fc.colorClass}${isActive ? " of-sp-active" : ""}`}
                    onClick={() => { setShowHidden(false); setSelectedStatus(prev => prev === fc.status ? null : fc.status); }}
                  >
                    <span className="of-sp-count">{count}</span>
                    <span className="of-sp-label">{fc.label}</span>
                  </button>
                );
              })}
              <button
                className={`of-status-pill of-sp-hidden${showHidden ? " of-sp-hidden-active" : ""}`}
                onClick={() => { setShowHidden(prev => !prev); setSelectedStatus(null); }}
              >
                <span className="of-sp-count">{hiddenCount}</span>
                <span className="of-sp-label">Hidden</span>
              </button>

              <MobileFilterDrawer
                summary={[filterMonth, filterRegion, filterState].filter(Boolean).join(" · ")}
                activeCount={[filterMonth, filterRegion, filterState].filter(Boolean).length}
                onReset={() => { setFilterMonth(""); setFilterRegion(""); setFilterState(""); }}
              >
                <div className="of-filter-right">
                  <span className="of-filter-section-label">FILTER</span>
                  <select className="of-filter-pill" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
                    <option value="">All Months</option>
                    {monthFilterOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <select className="of-filter-pill" value={filterRegion} onChange={e => { setFilterRegion(e.target.value); setFilterState(""); }}>
                    <option value="">All Regions</option>
                    {regionOptions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select className="of-filter-pill" value={filterState} onChange={e => setFilterState(e.target.value)}>
                    <option value="">All States</option>
                    {stateFilterOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </MobileFilterDrawer>
            </div>
          )}

          {loading && <div className="of-loading">Loading orders…</div>}
          {error   && <div className="of-error">{error}</div>}

          {!loading && !error && orders.length === 0 && (
            <div className="of-empty">No orders yet. Click <strong>+ Add New Order</strong> to get started.</div>
          )}

          {!loading && !error && orders.length > 0 && (
            <div className="of-table-wrapper">
              <table className="of-table mobile-sticky-first-col">
                <thead>
                  <tr>
                    <th className="of-label-col of-thead-label"></th>
                    {filteredOrders.map(order => (
                      <th key={order.id} className="of-order-header-cell">
                        <div className="of-order-header">
                          <div className="of-order-po">
                            {order.copacker_po ? `CoPacker PO #${order.copacker_po}` : "New Order"}
                          </div>
                          <div className="of-order-meta">
                            {[order.state, order.distributor].filter(Boolean).join(" · ") || "—"}
                          </div>
                          {canEdit && (showHidden ? (
                            <button className="of-visibility-btn of-show-btn" onClick={e => { e.stopPropagation(); handleToggleVisible(order.id, true); }}>Show Order</button>
                          ) : (
                            <button className="of-visibility-btn of-hide-btn" onClick={e => { e.stopPropagation(); handleToggleVisible(order.id, false); }}>Hide Order</button>
                          ))}
                          {canEdit && (confirmDelete === order.id ? (
                            <div className="of-delete-confirm">
                              <span>Delete?</span>
                              <button className="of-del-yes" onClick={e => { e.stopPropagation(); handleDelete(order.id); }}>Yes</button>
                              <button className="of-del-no"  onClick={e => { e.stopPropagation(); setConfirmDelete(null); }}>No</button>
                            </div>
                          ) : (
                            <button className="of-delete-btn" onClick={e => { e.stopPropagation(); setConfirmDelete(order.id); }} title="Delete this order">✕</button>
                          ))}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  <tr className="of-section-row">
                    <td className="of-section-label" colSpan={filteredOrders.length + 1}><span className="of-section-label-text">Order Info</span></td>
                  </tr>

                  {FIELD_ROWS.map(row => (
                    <tr key={row.key} className="of-field-row">
                      <td className="of-label-col of-row-label">
                        {row.key === "copacker_po" ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {row.label}
                            <button
                              type="button"
                              onClick={openPoNumModal}
                              title="Configure Auto-Create CoPacker PO # and starting number"
                              className="of-chip-btn"
                            >
                              Starting #
                            </button>
                          </span>
                        ) : (
                          row.label
                        )}
                      </td>
                      {filteredOrders.map(order => (
                        <td key={order.id} className="of-data-cell">{renderFieldCell(order, row)}</td>
                      ))}
                    </tr>
                  ))}

                  <tr className="of-section-row">
                    <td className="of-section-label" colSpan={filteredOrders.length + 1}><span className="of-section-label-text">ProductSKU</span></td>
                  </tr>

                  {displaySkus.map((sku, idx) => {
                    const prevGroup = idx > 0 ? getSkuGroup(displaySkus[idx - 1]) : null;
                    const thisGroup = getSkuGroup(sku);
                    const showHeader = prevGroup !== thisGroup;
                    return (
                      <React.Fragment key={sku}>
                        {showHeader && (
                          <tr className="of-pkg-header-row">
                            <td colSpan={filteredOrders.length + 1} className="of-pkg-header-cell">
                              <span className="of-pkg-header-text">{thisGroup}</span>
                            </td>
                          </tr>
                        )}
                        {/* Zebra keys off the SKU index, NOT nth-child — this
                            tbody also holds the section rows, ten field rows,
                            a group header per package and the total rows, so
                            positional parity striped the wrong things. */}
                        <tr className={`of-sku-row${idx % 2 === 1 ? " of-row-alt" : ""}`}>
                          <td className="of-label-col of-sku-label">{sku}</td>
                          {filteredOrders.map(order => (
                            <td key={order.id} className="of-data-cell of-sku-data-cell">{renderQtyCell(order, sku)}</td>
                          ))}
                        </tr>
                      </React.Fragment>
                    );
                  })}

                  <tr className="of-section-row">
                    <td className="of-section-label" colSpan={filteredOrders.length + 1}><span className="of-section-label-text">Totals</span></td>
                  </tr>

                  {sizes.map(size => (
                    <tr className="of-total-row" key={size}>
                      <td className="of-label-col of-row-label">Total {size}</td>
                      {filteredOrders.map(order => (
                        <td key={order.id} className="of-data-cell of-total-cell">{totalBySize[order.id]?.[size] || 0}</td>
                      ))}
                    </tr>
                  ))}
                  <tr className="of-total-row of-grand-total-row">
                    <td className="of-label-col of-row-label">Total Cases</td>
                    {filteredOrders.map(order => (
                      <td key={order.id} className="of-data-cell of-total-cell">{totalCases[order.id] || 0}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      </div>

      {/* Distributor PO attachment modal */}
      {poModal && (
        <div className="of-modal-overlay" onClick={() => poModal.stage !== "saving" && setPoModal(null)}>
          <div className="of-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, width: "92%" }}>
            <div className="of-modal-header">
              <h3>Distributor PO</h3>
              <button className="of-modal-close" onClick={() => setPoModal(null)}>×</button>
            </div>
            <div className="of-modal-body">
              <div className="of-field">
                <label className="of-field-label">Dist PO #</label>
                <input
                  type="text"
                  value={poModal.distributorPo}
                  onChange={(e) => setPoModal(p => p ? { ...p, distributorPo: e.target.value } : p)}
                  disabled={poModal.stage === "saving"}
                  placeholder="Enter distributor PO #"
                  className="of-field-input"
                />
              </div>
              <div className="of-field">
                <label className="of-field-label">PO File</label>
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  disabled={poModal.stage === "saving"}
                  onChange={(e) => { const f = e.target.files?.[0] ?? null; setPoModal(p => p ? { ...p, fileObj: f } : p); }}
                  className="of-file-input"
                />
                {poModal.fileObj && (
                  <div className="of-file-ready">
                    Ready to upload: <strong>{poModal.fileObj.name}</strong> ({Math.round((poModal.fileObj.size || 0) / 1024).toLocaleString()} KB)
                  </div>
                )}
              </div>

              {poModal.order?.po_file_path && !poModal.fileObj && (
                <div className="of-note of-note-ok">
                  <div style={{ marginBottom: 4 }}>
                    <strong>Currently attached:</strong> {poModal.order.po_file_name || "(unnamed file)"}
                    {poModal.order.po_file_size != null ? ` · ${Math.round(poModal.order.po_file_size / 1024).toLocaleString()} KB` : ""}
                  </div>
                  {poModal.order.po_file_uploaded_at && (
                    <div className="of-note-sub">
                      Uploaded {new Date(poModal.order.po_file_uploaded_at).toLocaleString()}
                      {poModal.order.po_file_uploaded_by ? ` by ${poModal.order.po_file_uploaded_by}` : ""}
                    </div>
                  )}
                </div>
              )}

              {(poModal.loadingPreview || poModal.currentUrl) && !poModal.fileObj && (
                <div className="of-preview">
                  {poModal.loadingPreview && <span className="of-preview-msg">Loading preview…</span>}
                  {!poModal.loadingPreview && poModal.currentUrl && (() => {
                    const ct = poModal.currentFile?.po_file_content_type || poModal.order?.po_file_content_type || "";
                    if (ct.startsWith("image/")) return <img src={poModal.currentUrl} alt="Attached PO preview" />;
                    if (ct === "application/pdf") return <iframe src={poModal.currentUrl} title="Attached PO preview" />;
                    return <a href={poModal.currentUrl} target="_blank" rel="noreferrer" className="of-preview-link">Open attachment in new tab →</a>;
                  })()}
                </div>
              )}

              {poModal.error && (
                <div className="of-note of-note-error">{poModal.error}</div>
              )}
            </div>
            <div className="of-modal-actions" style={{ justifyContent: "space-between" }}>
              <div>
                {poModal.order?.po_file_path && (
                  <button className="of-btn-ghost of-btn-danger" disabled={poModal.stage === "saving"} onClick={handlePoModalClear}>Remove File</button>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="of-btn-ghost" disabled={poModal.stage === "saving"} onClick={() => setPoModal(null)}>Cancel</button>
                <button
                  className="of-btn-primary"
                  disabled={poModal.stage === "saving" || (!poModal.fileObj && (poModal.distributorPo ?? "").trim() === (poModal.order.distributor_po ?? ""))}
                  onClick={handlePoModalSave}
                >
                  {poModal.stage === "saving" ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto CoPacker PO # settings modal */}
      {poNumModal && (
        <div className="of-modal-overlay" onClick={() => poNumModal.stage !== "saving" && setPoNumModal(null)}>
          <div className="of-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440, width: "92%" }}>
            <div className="of-modal-header">
              <h3>Auto CoPacker PO # Settings</h3>
              <button className="of-modal-close" onClick={() => setPoNumModal(null)}>×</button>
            </div>
            <div className="of-modal-body">
              {poNumModal.stage === "loading" ? (
                <div className="of-modal-loading">Loading…</div>
              ) : (
                <>
                  <label className="of-check-row">
                    <input
                      type="checkbox"
                      checked={!!poNumModal.enabled}
                      disabled={poNumModal.stage === "saving"}
                      onChange={(e) => setPoNumModal(p => p ? { ...p, enabled: e.target.checked } : p)}
                    />
                    <span>Auto Create CoPacker PO#</span>
                  </label>
                  <div className="of-field-tight">
                    <label className="of-field-label">Starting PO #</label>
                    <input
                      type="number" min="0" step="1"
                      value={poNumModal.starting}
                      disabled={poNumModal.stage === "saving"}
                      onChange={(e) => setPoNumModal(p => p ? { ...p, starting: e.target.value } : p)}
                      className="of-field-input"
                    />
                  </div>
                  <div className="of-hint">
                    Last assigned: {poNumModal.lastAssigned || "—"}.{" "}
                    {Number(poNumModal.starting) > Number(poNumModal.lastAssigned)
                      ? `Next new order will receive CoPacker PO # ${parseInt(poNumModal.starting, 10) || 0}.`
                      : `Next new order will receive CoPacker PO # ${(Number(poNumModal.lastAssigned) || 0) + 1}.`}
                  </div>
                  {poNumModal.error && (
                    <div className="of-note of-note-error">{poNumModal.error}</div>
                  )}
                </>
              )}
            </div>
            <div className="of-modal-actions">
              <button className="of-btn-ghost" disabled={poNumModal.stage === "saving"} onClick={() => setPoNumModal(null)}>Cancel</button>
              <button className="of-btn-primary" disabled={poNumModal.stage === "saving" || poNumModal.stage === "loading"} onClick={handlePoNumModalSave}>
                {poNumModal.stage === "saving" ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
